import React, {useCallback, useState} from 'react'
import {Dictionary, filter, join, mapValues, truncate} from 'lodash'
import {useQuery} from '@apollo/client'
import {RouteComponentProps} from '@reach/router'
import {useResizeDetector} from 'react-resize-detector'

import {useCart} from '../../contexts/CartContext'
import {ensureList} from '../../utils/typing'
import {Loader, Page, Container} from '../layout'
import {productDetailsQuery} from './queries'
import {
  ProductDetails,
  ProductDetailsVariables,
  ProductDetails_product,
  ProductDetails_product_variants,
} from './_gql-types/ProductDetails'
import {formatCurrency} from '../../utils/numbers'
import {getCartQuantityMap} from '../../utils/cart'
import { getCheckoutErrorMessage } from '../../api/saleor/errors'
import Alert from '../common/Alert'
import ProductImageGallery from './ProductImageGallery'
import DraftContent from '../common/DraftContent'


interface Props extends RouteComponentProps {
  slug?: string
}

interface ProductVariantsProps {
  variants: ProductDetails_product_variants[]
}

function ProductVariants({variants}: ProductVariantsProps) {
  const {cart, cartUpdate} = useCart()

  const variantsInCart = getCartQuantityMap(cart, variants.map((v) => v.id))

  const initialState = mapValues(variantsInCart, (v) => `${v || ''}`)

  const [state, setState] = useState<Dictionary<string>>(initialState)
  const [errors, setErrors] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const someInCart = !!Object.values(filter(variantsInCart)).length

  const hasChanges = variants.some((v) => (
    (variantsInCart[v.id] || 0) !== (parseInt(state[v.id] || '0', 10))
  ))

  const onSubmit = useCallback(async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    const res = await cartUpdate(mapValues(state, (v) => parseInt(v || '0', 10)))

    if (res?.checkoutErrors) {
      const errorMessages = res.checkoutErrors.map((err) => getCheckoutErrorMessage(err.code))
      setErrors(errorMessages)
    }

    setIsSubmitting(false)
  }, [state, cartUpdate, setErrors])

  const onQtyChange = useCallback<(e: React.ChangeEvent<HTMLInputElement>) => void>((e) => {
    const {value, name} = e.target

    setErrors([])
    setState({
      ...state,
      [name]: truncate(`${value.replace(/\D/g,'')}`, {length: 7, omission: ''}),
    })
  }, [state, setState])

  const firstVariantId = variants[0].id

  const values = {
    ...variantsInCart,
    ...state,
  }

  return (
    <form onSubmit={onSubmit}>
      {variants.length > 1 ? variants.map(({id, name}) => {
        const inputId = `id__cart_variant__${id}`

        return (
          <div className="row mb-4" key={id}>
            <div className="col">
              <label htmlFor={inputId} className="form-label">{name}</label>
            </div>

            <div className="col-4">
              <input
                className="form-control"
                type="number"
                placeholder="Magn"
                id={inputId}
                name={id}
                onChange={onQtyChange}
                value={values[id]}
                min={0}
              />
            </div>
          </div>
        )
      }) : (
        <div className="mb-4">
          <input
            className="form-control"
            type="number"
            placeholder="Magn"
            name={firstVariantId}
            onChange={onQtyChange}
            value={values[firstVariantId] || ''}
            min={0}
          />
        </div>
      )}

      <button
        type="submit"
        className="btn btn-primary btn-lg w-100 d-flex align-items-center justify-content-center"
        disabled={!hasChanges || isSubmitting}
      >
        {isSubmitting && (
          <div className="spinner-border spinner-border-sm text-white mr-3" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        )}
        {!someInCart ? 'Setja í körfu' : 'Uppfæra körfu'}
      </button>

      {errors.map((msg) => (
        <Alert key={msg} className="mt-4" type="danger" dismissible>{msg}</Alert>
      ))}
    </form>
  )
}

interface ProductProps {
  width?: number
  product: ProductDetails_product
}

function Product({
  width,
  product,
}: ProductProps) {
  const {name, pricing} = product
  const variants = ensureList(product.variants)
  const isInStock = variants.some(v => v.quantityAvailable)

  const sku = join(variants.map((variant) => variant.sku), ', ')

  // TODO: Show parent category if there is one
  const category = product.category?.name

  const originalPrice = pricing?.priceRangeUndiscounted?.start?.net.amount || 0
  const myPrice = pricing?.priceRange?.start?.net.amount || originalPrice

  const hasDiscount = myPrice < originalPrice

  const originalPriceFormatted = formatCurrency(originalPrice)
  const myPriceFormatted = formatCurrency(myPrice)

  return (
    <div className="row py-5">
      <div className="col-12 col-md-6 pr-md-5 mb-4">
        <div className="bg-white rounded p-3">
          <ProductImageGallery width={width} images={product.images}/>
        </div>
      </div>

      <div className="col-12 col-md-6">
        <div className="mb-5">
          <h5 className="mb-4"><span className="badge bg-dark large">{category}</span></h5>
          <h1 className="mb-4">{name}</h1>
          <h6 className="mb-5 font-weight-bold">Vörunúmer: {sku}</h6>
          <DraftContent data={product.descriptionJson} />
        </div>

        <h3 className="mb-5 font-weight-bold">
          {hasDiscount && (
            <s className="text-muted mr-3">{originalPriceFormatted} kr.</s>
          )}
          {myPriceFormatted} kr.
        </h3>

        <div className="border border-gray rounded p-4">
          <div className="mb-3">
            {isInStock ? (
              <span className="badge bg-success">Til á lager</span>
            ) : (
              <span className="badge bg-danger">Ekki til á lager</span>
            )}
          </div>

          <ProductVariants variants={variants} />
        </div>
      </div>
    </div>
  )
}

export default function ProductDetailsPage({slug}: Props) {
  slug = slug as string
  const {data, loading} = useQuery<ProductDetails, ProductDetailsVariables>(
    productDetailsQuery,
    {variables: {slug}},
  )

  const {width, ref} = useResizeDetector()

  const product = data?.product

  return (
    <Page>
      <Container size="lg">
        <div ref={ref}>
          {loading ? (
            <Loader />
          ) : (
            !product ? (
              <div className="card text-center">
                <div className="card-body">
                  <h2>Þessi vara er ekki til</h2>
                </div>
              </div>
            ) : (
              <Product width={width} product={product} />
            )
          )}
        </div>
      </Container>
    </Page>
  )
}
